import type {ReactElement} from 'react';
import {useState} from 'react';
import {ControlledList} from '@Components/controlled-list';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {confirmAndDeleteGeneratedImage, getGeneratedImageMenuItems} from '@Libraries/text-to-image-library';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {copyToClipboard} from '@Utils/clipboard.util';
import type {GeneratedImageItemMenuProps} from './generated-image-item-menu.types';
import {GENERATED_IMAGE_ITEM_MENU_MODAL_PANEL_ID} from './generated-image-item-menu.types';
import styles from './generated-image-item-menu.module.scss';

export function GeneratedImageItemMenu(props: GeneratedImageItemMenuProps): ReactElement {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPromptCopied, setIsPromptCopied] = useState(false);
  const modalPanelId = `${GENERATED_IMAGE_ITEM_MENU_MODAL_PANEL_ID}-${props.item.id}`;
  const closeModalForSmallScreen = useDefaultPanelClose(modalPanelId);

  const closeMenu = (): void => {
    setIsMenuOpen(false);
    closeModalForSmallScreen();
  };

  const onEditWithAi = (): void => {
    closeMenu();
    props.onEditWithAi?.(props.item);
  };

  const onCopyPrompt = (): void => {
    setIsPromptCopied(true);
    void copyToClipboard(props.item.prompt.text);
  };

  const onDelete = (): void => {
    closeMenu();
    confirmAndDeleteGeneratedImage(props.item, props.gridId, props.gridSelectionGroupId);
  };

  const getMenuContent = (): ReactElement => {
    return (
      <ControlledList
        className={`spacing-m-0 ${styles.popup}`}
        items={getGeneratedImageMenuItems({
          onEditWithAi: props.onEditWithAi ? onEditWithAi : undefined,
          onCopyPrompt,
          onDelete,
          isPromptCopied,
        })}
      />
    );
  };

  return (
    <div
      className={`${styles.menuAnchor} ${isMenuOpen ? styles.menuOpen : ''}`}
      onClick={(e): void => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onPointerDown={(e): void => {
        e.stopPropagation();
      }}
    >
      <Dropdown
        open={isMenuOpen}
        onOpen={(): void => {
          setIsMenuOpen(true);
        }}
        onClose={(): void => {
          setIsMenuOpen(false);
          setIsPromptCopied(false);
        }}
        doesCloseOnOuterClick
        doCloseOnInnerClick={false}
        selector={<Icon icon="icon-menu-dots" isActive={isMenuOpen} className={'_cursor-pointer'} size={IconSize.SIZE_ICON_16} type={IconType.SECONDARY} shape={IconShape.SQUARE} />}
        popup={getMenuContent()}
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        position={DROPDOWN_POSITION.FIXED}
        popupOffset={8}
        popUpHasCustomWidth
        showModalForSmallScreen
        modalTitle={window.i18next.t('pmwjs_options')}
        panelIdForModal={modalPanelId}
        modalContent={getMenuContent()}
      />
    </div>
  );
}
